import {
  QueryClient,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { createContext, useContext, type ReactNode } from 'react';
import { isApiError } from '../problem';
import type { WebApi } from './client';
import type {
  Activation,
  ActivationRequest,
  AdherenceTrendQuery,
  ClientPage,
  ListClientsQuery,
  ListDevicesQuery,
} from './types';

const WebApiContext = createContext<WebApi | undefined>(undefined);

export function WebApiProvider({ api, children }: { api: WebApi; children: ReactNode }) {
  return <WebApiContext.Provider value={api}>{children}</WebApiContext.Provider>;
}

export function useWebApi(): WebApi {
  const api = useContext(WebApiContext);
  if (!api) throw new Error('useWebApi must be used inside WebApiProvider.');
  return api;
}

/**
 * Query defaults for the web shells. A refused request (4xx) or one not built yet (501) is never retried; a
 * transient failure is retried once.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (isApiError(error) && (error.status < 500 || error.status === 501)) return false;
          return failureCount < 1;
        },
      },
    },
  });
}

/** Query keys, scoped under `web` so another BFF's cache never collides. */
export const webQueryKeys = {
  me: () => ['web', 'me'] as const,
  attentionSummary: () => ['web', 'attention-summary'] as const,
  clients: (query: Omit<ListClientsQuery, 'cursor'>) => ['web', 'clients', query] as const,
  client: (clientId: string) => ['web', 'client', clientId] as const,
  adherenceWeek: (clientId: string, weekStart: string | undefined) =>
    ['web', 'client', clientId, 'adherence', weekStart ?? 'current'] as const,
  careTeam: (clientId: string) => ['web', 'client', clientId, 'care-team'] as const,
  adherenceTrend: (clientId: string, query: AdherenceTrendQuery) =>
    ['web', 'client', clientId, 'adherence-trend', query] as const,
  medicationProfile: (clientId: string) => ['web', 'client', clientId, 'medication-profile'] as const,
  doseWindows: (clientId: string) => ['web', 'client', clientId, 'dose-windows'] as const,
  carerMatches: (mobile: string) => ['web', 'carer-matches', mobile] as const,
  devices: (query: ListDevicesQuery) => ['web', 'devices', query] as const,
  activation: (activationId: string) => ['web', 'activation', activationId] as const,
};

/** The signed-in user, role, active pharmacy and permissions (W1). */
export function useMe(options: { enabled?: boolean } = {}) {
  const api = useWebApi();
  return useQuery({
    queryKey: webQueryKeys.me(),
    queryFn: ({ signal }) => api.getMe(signal),
    staleTime: 5 * 60_000,
    enabled: options.enabled ?? true,
  });
}

/** Dashboard tile counts and pharmacy-wide adherence (W2). */
export function useAttentionSummary() {
  const api = useWebApi();
  return useQuery({
    queryKey: webQueryKeys.attentionSummary(),
    queryFn: ({ signal }) => api.getAttentionSummary(signal),
  });
}

/**
 * The active pharmacy's clients, a page at a time with the contract's opaque cursor (W2, W3). `q` under two
 * characters is not sent; the contract requires at least two.
 */
export function useClients(query: Omit<ListClientsQuery, 'cursor'>) {
  const api = useWebApi();
  const q = query.q?.trim();
  const params: Omit<ListClientsQuery, 'cursor'> = { ...query, q: q && q.length >= 2 ? q : undefined };
  return useInfiniteQuery<
    ClientPage,
    Error,
    InfiniteData<ClientPage>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: webQueryKeys.clients(params),
    queryFn: ({ pageParam, signal }) => api.listClients({ ...params, cursor: pageParam }, signal),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}

/** A client's overview and identifiers (W3). */
export function useClient(clientId: string) {
  const api = useWebApi();
  return useQuery({
    queryKey: webQueryKeys.client(clientId),
    queryFn: ({ signal }) => api.getClient(clientId, signal),
  });
}

/** One pack week of dose outcomes for the client's MCM pack (W3). Undefined `weekStart` means the current week. */
export function useClientAdherenceWeek(
  clientId: string,
  weekStart?: string,
  options: { enabled?: boolean } = {},
) {
  const api = useWebApi();
  return useQuery({
    queryKey: webQueryKeys.adherenceWeek(clientId, weekStart),
    queryFn: ({ signal }) => api.getClientAdherenceWeek(clientId, weekStart, signal),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

/** The client's care team, view-only (W3). */
export function useClientCareTeam(clientId: string) {
  const api = useWebApi();
  return useQuery({
    queryKey: webQueryKeys.careTeam(clientId),
    queryFn: ({ signal }) => api.getClientCareTeam(clientId, signal),
  });
}

/** A client's adherence trend, grouped by day, week or month, for the adherence chart (W3). */
export function useClientAdherenceTrend(
  clientId: string,
  query: AdherenceTrendQuery,
  options: { enabled?: boolean } = {},
) {
  const api = useWebApi();
  return useQuery({
    queryKey: webQueryKeys.adherenceTrend(clientId, query),
    queryFn: ({ signal }) => api.getClientAdherenceTrend(clientId, query, signal),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

/** The client's medication and pack lists, read-only from the on-premise MMS (W4). */
export function useClientMedicationProfile(clientId: string) {
  const api = useWebApi();
  return useQuery({
    queryKey: webQueryKeys.medicationProfile(clientId),
    queryFn: ({ signal }) => api.getClientMedicationProfile(clientId, signal),
  });
}

/** Pack 1's dose slots, the pharmacy's slot times and the computed windows, read-only (W4, W5). */
export function useClientDoseWindows(clientId: string, options: { enabled?: boolean } = {}) {
  const api = useWebApi();
  return useQuery({
    queryKey: webQueryKeys.doseWindows(clientId),
    queryFn: ({ signal }) => api.getClientDoseWindows(clientId, signal),
    enabled: options.enabled ?? true,
  });
}

/** Whether a carer with this mobile (E.164) is already registered: zero or one match, minimal detail (W5). */
export function useCarerMatches(mobile: string, options: { enabled?: boolean } = {}) {
  const api = useWebApi();
  return useQuery({
    queryKey: webQueryKeys.carerMatches(mobile),
    queryFn: ({ signal }) => api.listCarerMatches(mobile, signal),
    enabled: options.enabled ?? true,
  });
}

/** One page of the pharmacy's MCM devices: available stock, or the device with a scanned serial (W5, W6). */
export function useDevices(query: ListDevicesQuery, options: { enabled?: boolean } = {}) {
  const api = useWebApi();
  return useQuery({
    queryKey: webQueryKeys.devices(query),
    queryFn: ({ signal }) => api.listDevices(query, signal),
    enabled: options.enabled ?? true,
  });
}

/** An earlier activation, with each carer's invitation status (W5). */
export function useActivation(activationId: string, options: { enabled?: boolean } = {}) {
  const api = useWebApi();
  return useQuery({
    queryKey: webQueryKeys.activation(activationId),
    queryFn: ({ signal }) => api.getActivation(activationId, signal),
    enabled: options.enabled ?? true,
  });
}

/**
 * Activate MCM for a client (W5). Pass the same `idempotencyKey` when retrying one attempt. On success, every
 * cached view the activation changes is refetched: the client lists and dashboard counts, the client's own record
 * and care team, the device list and carer matches.
 */
export function useCreateActivation() {
  const api = useWebApi();
  const queryClient = useQueryClient();
  return useMutation<Activation, Error, { request: ActivationRequest; idempotencyKey: string }>({
    mutationFn: ({ request, idempotencyKey }) => api.createActivation(request, idempotencyKey),
    onSuccess: (activation) => {
      for (const queryKey of [
        ['web', 'clients'],
        webQueryKeys.attentionSummary(),
        webQueryKeys.client(activation.clientId),
        ['web', 'devices'],
        ['web', 'carer-matches'],
      ]) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
